import {
  Box,
  Button,
  IconButton,
  Typography,
  ThemeProvider,
  SelectChangeEvent,
} from "@mui/material";
import "../style.css";
import moment from "moment";
import { useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import ROUTE_NAMES from "routes/routeNames";
import { getTableTheme } from "../tableTheme";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency } from "utils/commonFunctions";
import { commonTableStyles as styles } from "../styles";
import { OrderListItem, ReducersModal, UserDetailModal } from "utils/modals";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { dispatchOrderValue } from "modules/orderManagement/action";
import UserDopdown from "components/userDropdown";
import { getPaymentModeLabel, USER_TYPE } from "utils/userType";
import { CATEGORY_DATA, ORDER_STATUS_DATA, PAYMENT_DATA } from "utils/constants";
import TableFilterSelect from "../TableFilterSelect";
import { BRAND } from "themes";

const filterFieldSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    fontSize: 14,
    color: BRAND.ink,
    "& fieldset": { borderColor: BRAND.border },
    "&:hover fieldset": { borderColor: "#DDD8EE" },
    "&.Mui-focused fieldset": { borderColor: BRAND.primary, borderWidth: "1px" },
  },
};

const nameButtonSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  margin: 0,
  padding: 0,
  border: 0,
  background: "transparent",
  color: BRAND.ink,
  font: "inherit",
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "20px",
  cursor: "pointer",
  textAlign: "left" as const,
  "&:hover": { color: BRAND.primary },
  "&:focus-visible": {
    outline: `2px solid ${BRAND.primary}`,
    outlineOffset: 2,
    borderRadius: "8px",
  },
};

const avatarSx = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  color: "#fff",
  backgroundColor: BRAND.primary,
  fontWeight: 700,
  fontSize: 12,
  lineHeight: 1,
};

const actionButtonSx = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  border: `1px solid ${BRAND.border}`,
  color: BRAND.primary,
  "&:hover": { backgroundColor: "#F7F5FF" },
};

const createButtonSx = {
  height: 40,
  marginLeft: "12px",
  padding: "6px 14px",
  borderRadius: "12px",
  boxShadow: "none",
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 14,
  backgroundColor: BRAND.primary,
  "&:hover": { boxShadow: "none", backgroundColor: BRAND.primaryDark },
};

const pillSx = {
  padding: "4px 10px",
  borderRadius: "999px",
  backgroundColor: "#EDE9FE",
  color: BRAND.primary,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: "16px",
};

const initials = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) || "";
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase() || "U";
};

const orderDetailPath = (orderId: string) =>
  `${ROUTE_NAMES.MAIN_ROUTES.ORDER_DETAIL}?orderId=${orderId}`;

type OrderTableProps = {
  onAddPress?: () => void;
};

const OrderTable = ({ onAddPress }: OrderTableProps) => {
  const {
    limit,
    page,
    result,
    search,
    serviceTypeId,
    totalRecords,
    orderStatus,
    serviceTypesList,
    paymentType,
    userId,
    merchantId,
    category,
    selectedUser,
    selectedMerchant,
  } = useSelector((state: ReducersModal) => state.OrderManagementReducer);

  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const dispatchSearchValue = (text: string) => {
    dispatch(dispatchOrderValue({ search: text }));
  };

  const handleChangePage_orderTable = (newPage: number) => {
    dispatch(dispatchOrderValue({ page: newPage + 1 }));
  };

  const handleChangeRowsPerPage_orderTable = (numberOfRows: number) => {
    dispatch(dispatchOrderValue({ limit: numberOfRows, page: 1 }));
  };

  const serviceTypes = useMemo(() => {
    return serviceTypesList?.map((data: any) => {
      return { id: data.id, name: data.serviceType };
    });
  }, [serviceTypesList]);

  const orderStatusHandler = (event: SelectChangeEvent) => {
    dispatch(dispatchOrderValue({ orderStatus: event.target.value, page: 1 }));
  };

  const paymentTypeHandler = (event: SelectChangeEvent) => {
    dispatch(dispatchOrderValue({ paymentType: event.target.value, page: 1 }));
  };

  const categoryHandler = (event: SelectChangeEvent) => {
    dispatch(dispatchOrderValue({ category: event.target.value, page: 1 }));
  };

  const userHandler = (user: UserDetailModal | null) => {
    dispatch(
      dispatchOrderValue({
        userId: user?.id || "",
        selectedUser: user,
        page: 1,
      })
    );
  };

  const merchantHandler = (user: UserDetailModal | null) => {
    dispatch(
      dispatchOrderValue({
        merchantId: user?.id || "",
        selectedMerchant: user,
        page: 1,
      })
    );
  };

  const resetFilters = () => {
    dispatch(
      dispatchOrderValue({
        serviceTypeId: "-1",
        orderStatus: "-1",
        paymentType: "-1",
        userId: "",
        merchantId: "",
        category: "-1",
        selectedUser: null,
        selectedMerchant: null,
        page: 1,
      })
    );
  };

  const [localSortOrder, setLocalSortOrder] = useState("desc");
  const sortHandler = (changedColumn: string, direction: string) => {
    setLocalSortOrder(direction);
    dispatch(
      dispatchOrderValue({
        sortedBy: changedColumn,
        sortOrder: direction,
      })
    );
  };

  const columns: any = [
    {
      name: "orderId",
      label: "Order Id",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any, tableMeta: any) => {
          const row: OrderListItem = tableMeta.tableData[tableMeta.rowIndex];
          if (value)
            return (
              <Box
                component="button"
                type="button"
                sx={nameButtonSx}
                onClick={() => navigate(orderDetailPath(row._id))}
              >
                {value}
              </Box>
            );
          else return <>N/A</>;
        },
      },
    },
    {
      name: "userName",
      label: "User Name",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any, tableMeta: any) => {
          const row: OrderListItem = tableMeta.tableData[tableMeta.rowIndex];
          if (value)
            return (
              <Box
                component="button"
                type="button"
                sx={nameButtonSx}
                onClick={() =>
                  navigate(
                    `${ROUTE_NAMES.MAIN_ROUTES.USER_DETAIL}?userId=${row.userId}`
                  )
                }
              >
                <Box sx={avatarSx} aria-hidden>
                  {initials(value)}
                </Box>
                {value}
              </Box>
            );
          else return <>N/A</>;
        },
      },
    },
    {
      name: "paymentMode",
      label: "Payment Mode",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => {
          return <div>{getPaymentModeLabel(value)}</div>;
        },
      },
    },
    {
      name: "price",
      label: "Price",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => {
          return <div>{formatCurrency(+value)}</div>;
        },
      },
    },
    {
      name: "service",
      label: "Service Type",
      options: {
        sort: false,
        filter: false,
      },
    },
    {
      name: "createdAt",
      label: "Created On",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any) => {
          return <div>{value ? moment(value).format("DD/MM/YYYY") : "N/A"}</div>;
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row: OrderListItem = tableMeta.tableData[tableMeta.rowIndex];
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <IconButton
                aria-label="Edit order"
                sx={actionButtonSx}
                onClick={() => {
                  navigate(
                    `${ROUTE_NAMES.MAIN_ROUTES.EDIT_ORDER}?orderId=${row._id}`,
                    {
                      state: {
                        orderDetails: row,
                      },
                    }
                  );
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                aria-label="View order"
                sx={actionButtonSx}
                onClick={() => navigate(orderDetailPath(row._id))}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          );
        },
      },
    },
    {
      name: "User",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: userId ? [userId] : [],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <UserDopdown
              appearance="filter"
              fieldSx={filterFieldSx}
              disablePortal
              label="User"
              userType={USER_TYPE.CUSTOMER}
              selectedUser={selectedUser}
              onSelectUser={userHandler}
            />
          ),
        },
      },
    },
    {
      name: "Franchise",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: merchantId ? [merchantId] : [],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <UserDopdown
              appearance="filter"
              fieldSx={filterFieldSx}
              disablePortal
              label="Franchise"
              userType={USER_TYPE.MERCHANT}
              selectedUser={selectedMerchant}
              onSelectUser={merchantHandler}
            />
          ),
        },
      },
    },
    {
      name: "Category",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: [category],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <TableFilterSelect
              fieldSx={filterFieldSx}
              label="Category"
              value={category}
              options={CATEGORY_DATA}
              onChange={categoryHandler}
            />
          ),
        },
      },
    },
    {
      name: "Order Status",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: ["-1", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <TableFilterSelect
              fieldSx={filterFieldSx}
              label="Order status"
              value={orderStatus}
              options={ORDER_STATUS_DATA}
              onChange={orderStatusHandler}
            />
          ),
        },
      },
    },
    {
      name: "Payment Type",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: ["-1", "0", "1", "2"],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <TableFilterSelect
              fieldSx={filterFieldSx}
              label="Payment type"
              value={paymentType}
              options={PAYMENT_DATA}
              onChange={paymentTypeHandler}
            />
          ),
        },
      },
    },
  ];

  const options: any = {
    print: false,
    filter: true,
    download: false,
    serverside: true,
    viewColumns: false,
    selectableRows: "none",
    filterType: "dropdown",
    sortOrder: {
      name: "createdAt",
      direction: localSortOrder,
    },
    count: totalRecords,
    page: page - 1,
    rowsPerPage: limit,
    onChangePage: (currentPage: number) =>
      handleChangePage_orderTable(currentPage),
    rowsPerPageOptions: [10, 25, 50, 100],
    onChangeRowsPerPage: (numberOfRows: number) =>
      handleChangeRowsPerPage_orderTable(numberOfRows),
    search: true,
    searchPlaceholder: "Search by Order ID...",
    textLabels: {
      filter: {
        title: "Filters",
        reset: "Reset",
      },
    },
    onSearchChange: (text: string) => {
      if (!(text?.length === 0 && search.length === 0)) {
        dispatchSearchValue(text);
      }
    },
    onFilterChange: (_: string, _filterList: any[], type: any) => {
      if (type === "reset") {
        resetFilters();
      }
    },
    onColumnSortChange: (changedColumn: string, direction: string) => {
      sortHandler(changedColumn, direction);
    },
    customToolbar: () =>
      onAddPress ? (
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          sx={createButtonSx}
          onClick={onAddPress}
        >
          Create order
        </Button>
      ) : null,
  };

  const statusLabel = ORDER_STATUS_DATA.find((item) => item.id === orderStatus)?.label;
  const serviceLabel = serviceTypes?.find((item) => item.id === serviceTypeId)?.name;

  const TableComponent: any = MUIDataTable;
  return (
    <Box
      sx={{
        ...styles.paper,
        marginTop: 0,
        borderRadius: "22px",
        boxShadow: "0 12px 28px -18px rgba(27, 19, 64, 0.45)",
      }}
    >
      <ThemeProvider theme={getTableTheme({ headerColor: "#5C5870", brandFilter: true })}>
        <TableComponent
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: "22px",
                  letterSpacing: "-0.02em",
                  color: BRAND.ink,
                }}
              >
                Orders
              </Typography>
              {orderStatus !== "-1" && statusLabel ? (
                <Box component="span" sx={pillSx}>
                  {statusLabel}
                </Box>
              ) : null}
              {serviceTypeId !== "-1" && serviceLabel ? (
                <Box component="span" sx={pillSx}>
                  {serviceLabel}
                </Box>
              ) : null}
            </Box>
          }
          data={result}
          columns={columns}
          options={options}
        />
      </ThemeProvider>
    </Box>
  );
};

export default OrderTable;
